import { Badge, type BadgeVariant } from "@design";
import { useTranslation } from "react-i18next";
import type { LicenseResponse, LicenseStatusResponse } from "@/api";
import { PageSection, StatRow } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatDate } from "@/i18n/format";

type LicenseStatusValue = "active" | "expired" | "suspended" | "seat_limit_reached";

const STATUS_VARIANT: Record<LicenseStatusValue, BadgeVariant> = {
  active: "success",
  expired: "danger",
  suspended: "danger",
  seat_limit_reached: "warning",
};

export const isSuspended = (status: LicenseStatusResponse) => status.status === "suspended";

/** Everyone's view: license state, seats, expiry and whether the platform is up. */
export function LicenseStatusSection({
  status,
  detail,
}: Readonly<{ status: LicenseStatusResponse; detail?: LicenseResponse }>) {
  const { t } = useTranslation("adminLicense");
  const value = status.status as LicenseStatusValue;
  const used = status.seatsUsed ?? 0;
  const suspended = isSuspended(status);
  const reason = detail?.suspensionReason;

  return (
    <PageSection title={detail?.licenseName || t("status.title")}>
      <StatRow
        stats={[
          {
            label: t("status.label"),
            value: (
              <Badge variant={STATUS_VARIANT[value] ?? "neutral"}>
                {t(`status.values.${value}`, { defaultValue: status.status })}
              </Badge>
            ),
          },
          {
            label: t("status.seats"),
            value:
              status.seatLimit == null
                ? t("status.seatsUnlimited", { used })
                : t("status.seatsOf", { used, limit: status.seatLimit }),
            hint:
              status.seatsAvailable == null ? undefined : t("status.seatsAvailable", { count: status.seatsAvailable }),
          },
          {
            label: t("status.expires"),
            value: status.expiresAt ? formatDate(status.expiresAt) : t("status.never"),
          },
          {
            label: t("status.platform"),
            value: (
              <Badge variant={suspended ? "danger" : "success"}>
                {suspended ? t("status.suspended") : t("status.online")}
              </Badge>
            ),
          },
        ]}
      />
      {suspended && reason && (
        <Text variant="body-sm" tone="error">
          {t("status.suspendedReason", { reason })}
        </Text>
      )}
    </PageSection>
  );
}
